import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Box, Typography, Table } from '@mui/material';
import Button from '../../../../components/UI/Button/Button';
import { IMailBox } from '../../../../models/site';
import './MailBoxesTable.scss';
import MailBoxesTableHead from './MailBoxesTableHead';
import MailBoxesTableBody from './MailBoxesTableBody';

interface MailBoxesTableProps {
  mailBoxes: IMailBox[] | undefined;
  rcTranslate: (key: string) => string;
  updateMailBoxNumber: (position: number | string, value: string) => void;
  removeOne: (position: number | string) => void;
}

const MailBoxesTable: React.FC<MailBoxesTableProps> = ({
  mailBoxes,
  rcTranslate,
  updateMailBoxNumber,
  removeOne,
}) => {
  const [draftValues, setDraftValues] = useState<Record<string, string>>({});
  const [dirtyPositions, setDirtyPositions] = useState<Set<number | string>>(
    new Set()
  );

  // Initialize / sync draft when mailBoxes prop changes (external updates or reset)
  useEffect(() => {
    const next: Record<string, string> = {};
    mailBoxes?.forEach((mb) => {
      next[mb.position.toString()] = mb.mailBoxNumber;
    });
    setDraftValues(next);
    // Clear dirty marks because prop is now source of truth
    setDirtyPositions(new Set());
  }, [mailBoxes]);

  const handleChange = (position: number | string, value: string) => {
    const key = position.toString();
    setDraftValues((prev) => ({ ...prev, [key]: value }));
    setDirtyPositions((prev) => {
      const next = new Set(prev);
      next.add(position);
      return next;
    });
  };

  const hasChanges = dirtyPositions.size > 0;

  const hasEmptyValues = useMemo(() => {
    if (!hasChanges) return false;
    for (const pos of dirtyPositions) {
      if (draftValues[pos.toString()] === '') return true;
    }
    return false;
  }, [dirtyPositions, draftValues, hasChanges]);

  const hasDuplicateValues = useMemo(() => {
    if (!hasChanges) return false;
    const seen = new Map(
      mailBoxes?.map((mb) => [mb.position.toString(), mb.mailBoxNumber])
    );
    for (const pos of dirtyPositions) {
      const val = draftValues[pos.toString()];
      seen.set(pos.toString(), val);
    }
    return seen.size !== new Set(seen.values()).size;
  }, [dirtyPositions, draftValues, hasChanges]);

  const handleSave = useCallback(() => {
    if (!hasChanges) return;
    dirtyPositions.forEach((pos) => {
      const key = pos.toString();
      const mbValue = draftValues[key];
      updateMailBoxNumber(pos, mbValue);
    });
    setDirtyPositions(new Set());
  }, [dirtyPositions, draftValues, hasChanges, updateMailBoxNumber]);

  const handleCancel = useCallback(() => {
    const reverted: Record<string, string> = {};
    mailBoxes?.forEach((mb) => {
      reverted[mb.position.toString()] = mb.mailBoxNumber;
    });
    setDraftValues(reverted);
    setDirtyPositions(new Set());
  }, [mailBoxes]);

  return (
    <Box className="mailBoxesEditor__list">
      <Typography className="mailBoxesEditor__title">
        {rcTranslate('sites.closetWithSiteDetails.mailBoxes.existingTitle')}
      </Typography>
      {!mailBoxes?.length && (
        <Typography className="mailBoxesEditor__empty">
          {rcTranslate('sites.closetWithSiteDetails.mailBoxes.none')}
        </Typography>
      )}
      {mailBoxes?.length ? (
        <Table className="mailBoxesEditor__table--small">
          <MailBoxesTableHead rcTranslate={rcTranslate} />
          <MailBoxesTableBody
            mailBoxes={mailBoxes}
            draftValues={draftValues}
            dirtyPositions={dirtyPositions}
            onChange={handleChange}
            onRemove={removeOne}
          />
        </Table>
      ) : null}
      {mailBoxes?.length ? (
        <Box className="mailBoxesEditor__actions">
          <Button
            title={'sites.closetWithSiteDetails.mailBoxes.saveButton'}
            onClick={handleSave}
            disabled={!hasChanges || hasEmptyValues || hasDuplicateValues}
          />
          <Button
            title={'sites.closetWithSiteDetails.mailBoxes.cancelButton'}
            onClick={handleCancel}
            disabled={!hasChanges}
            outlined
            isCancelButton
          />
        </Box>
      ) : null}
    </Box>
  );
};

export default MailBoxesTable;
